import { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Alert as MuiAlert,
  CircularProgress,
} from '@mui/material';
import { useApi } from '../../hooks/useApi';
import SheetsMidSelect from './SheetsMidSelect';

interface SheetsMidDialogProps {
  open: boolean;
  sheetsMidApiUrl: string;
  alertId: number;
  alertLabel: string | null;
  companyId: number;
  currentMid: string | null;
  onClose: () => void;
  onSave: () => void;
}

export default function SheetsMidDialog({
  open,
  sheetsMidApiUrl,
  alertId,
  alertLabel,
  companyId,
  currentMid,
  onClose,
  onSave,
}: SheetsMidDialogProps) {
  const { patch, loading, error } = useApi();
  const [mid, setMid] = useState<string>(currentMid ?? '');

  const handleClose = (_event: unknown, reason?: string) => {
    if (reason === 'backdropClick') return;
    onClose();
  };

  const handleSubmit = async () => {
    if (loading) return;
    try {
      await patch(sheetsMidApiUrl, { alertId, googleSheetMid: mid });
      onSave();
    } catch (err) {
      console.error('Failed to update Sheets MID:', err);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} aria-labelledby="sheets-mid-dialog-title" maxWidth="xs" fullWidth>
      <DialogTitle id="sheets-mid-dialog-title">
        Edit Sheets MID — Alert #{alertLabel}
      </DialogTitle>
      <DialogContent>
        {error && (
          <MuiAlert severity="error" sx={{ mb: 2 }}>
            {error}
          </MuiAlert>
        )}
        <Box sx={{ mt: 1 }}>
          <SheetsMidSelect
            companyId={companyId}
            value={mid}
            onChange={setMid}
            disabled={loading}
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} color="inherit" disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          disabled={loading || mid === (currentMid ?? '')}
          startIcon={loading ? <CircularProgress size={16} /> : null}
        >
          {loading ? 'Saving...' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
